import React, { useEffect, useState } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import {
  useAddActivity,
  useDocumentFile,
  useGetAllDocumentsList,
  useGetAssessmenttDetails,
  UseGetRfiListForFilters,
  useGetVQAActivity,
  useGetassessmentDetails,
  useVQASponsorContactAdmin,
  useDocumentAllFiles,
  useGetVQAActivityBeforeAccepting,
  useRequestCancelAssessment,
  useCancelAssessment,
} from "../custom-hooks/queries";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { calculateDaysAgo, dateFormat } from "../utils/helperMethods";
import { Switch } from "@headlessui/react";
import UploadReportModal from "../components/uploadReportModal";
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  DocumentIcon,
  FolderPlusIcon,
  MapPinIcon,
  PaperClipIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import CustomTooltip from "../components/Filters/CustomTooltip";
import Notification from "../components/Notification";
import CustomButton from "../components/CustomButton";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Capabilities from "../components/Capabilities";
import StatusProgressBar from "../components/ProgressBar";
import ProfileDrawer from "../components/ProfileDrawer";
import { Icon } from "@iconify/react/dist/iconify.js";
import AdminDetailsLoader from "../components/Skeletons/adminDetailsLoader";
import ReportAndActivitySkeletonLoader from "../components/Skeletons/ReportAndActivitySkeletonLoader";
import Skeleton from "react-loading-skeleton";
import JSZip from "jszip";
import StatusForDrawer from "../components/StatusForDrawer";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import Modal from "../components/Modal";
import NotificationModal from "../components/NotificationModal";
import { assessmentStatusDropdown } from "../utils/constants";
import { ReportContainer } from "../components/ReportContainer";
import { getCountryByValue, getStateOptions } from "../utils/locationData";
import { getVQALocationFromProvider } from "../utils/addressUtils";

const schema = yup.object().shape({
  sponsor_contact_name: yup.string(),
  sponsor_contact_email: yup.string().email("Invalid email format"),
  sponsor_contact_phone: yup
    .string()
    .matches(/^[0-9]+$/, "Phone number must contain only numbers")
    .min(10, "Phone number must be at least 10 digits"),
});

export const SponsorDetailAssessment = () => {
  const { id } = useParams();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(false);
  const navigate = useNavigate();
  const animatedComponents = makeAnimated();
  const { data: rfis } = UseGetRfiListForFilters();

  const {
    data: assessmentDetails,
    isLoading,
    refetch: assessmentDetailsRefetch,
  } = useGetAssessmenttDetails(id);

  const optionsForMultiSelect = rfis?.map((item) => ({
    value: item?.rfi_id,
    label: item?.rfi_name,
  }));
  const [show, setShow] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [comment, setComment] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const { data: activityBeforeAccept, refetch: activityRefetchBeforeAccept } =
    useGetVQAActivityBeforeAccepting(assessmentDetails?.vqa_request_id, {
      enabled: !!assessmentDetails?.vqa_request_id, // Only enable the query when vqa_id exists
    });

  const { data: activity, refetch: activityRefetch } = useGetVQAActivity(
    assessmentDetails?.vqa?.vqa_id,
    {
      enabled: !!assessmentDetails?.vqa?.vqa_id,
    }
  );

  const { data: allDocuments } = useGetAllDocumentsList(
    1,
    null,
    null,
    null,
    null,
    null,
    null,
    assessmentDetails?.vqa?.vqa_id
  );

  useEffect(() => {
    if (assessmentDetails?.vqa?.vqa_id && allDocuments) {
      activityRefetch();
    }
  }, [
    assessmentDetails,
    allDocuments,
    assessmentDetails?.vqa?.vqa_id,
    activityRefetch,
  ]);

  useEffect(() => {
    activityRefetchBeforeAccept();
  }, [assessmentDetails]);

  const {
    mutate: allFilesDownload,
    data: allFiles,
    isError: allFilesError,
  } = useDocumentAllFiles();

  const { mutate: add } = useAddActivity();

  const handleDownloadAll = async (ids) => {
    allFilesDownload(ids); // Pass ids directly instead of using docsIds (state update is async)
  };

  const handleChange = (e) => {
    setComment(e.target.value);
  };

  const addComment = () => {
    add(
      { vqa_id: assessmentDetails?.vqa?.vqa_id, comment },
      {
        onSuccess: () => {
          setComment("");
          activityRefetch();
        },
      }
    );
  };
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // Hide 'Report Anonymized' for sponsors
  // If user is a sponsor, filter out 'Report Anonymized' documents
  // (Assume sponsor role is determined by a prop, context, or global state. Here, we check if vqa_request_sponsors exists and has length > 0)
  let filteredReports = allDocuments?.items || [];
  if (assessmentDetails?.vqa_request_sponsors?.length > 0) {
    filteredReports = filteredReports.filter(
      (document) => document?.document_sub_type !== "Report Anonymized"
    );
  }
  // If any sponsor has "Canceled Assessment" status, also filter out CAPA Response Report
  const hasCancelledAssessment = assessmentDetails?.vqa_request_sponsors?.some(
    (sponsor) => sponsor?.sponsor_status === "Canceled Assessment"
  );
  if (hasCancelledAssessment) {
    filteredReports = filteredReports.filter(
      (document) => document?.document_sub_type !== "CAPA Response Report"
    );
  }

  return (
    <div className="container max-w-full px-4 sm:px-5 lg:px-6 custom-breakpoint-7:px-6 custom-breakpoint-9:px-8">
      {isLoading ? (
        <Skeleton
          className="w-full h-32 p-4 sm:p-7 border-y border-gray-300 bg-slate-50 mb-10"
          enableAnimation={true}
        />
      ) : (
        <div className="p-4 sm:p-7 custom-breakpoint-7:p-6 custom-breakpoint-9:p-8 border-b border-gray-300 bg-slate-50 mb-10">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <h1
              className="text-gray-500 cursor-pointer font-semibold"
              onClick={() => navigate("/Assessment/History")}
            >
              Assessments
            </h1>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            <p className="ml-5 font-semibold">
              <p>
                <span className="font-semibold">
                  {assessmentDetails?.vqa_request_reference_number}{" "}
                </span>{" "}
                <span>{assessmentDetails?.vqa_reference_number}</span>
              </p>{" "}
            </p>
          </div>
          <div className="mt-5 space-y-2 flex flex-col justify-start items-start md:justify-between md:items-center md:flex-row gap-4 md:gap-0">
            <div className="flex items-center min-w-0">
              <div></div>
              <span className="bg-white border border-gray-300 rounded-md px-4 sm:px-7 py-5 mr-3 sm:mr-5 flex-shrink-0">
                <CheckBadgeIcon className="h-9 w-9 text-red-500" />
              </span>

              <span className="min-w-0">
                <p className="text-sm text-gray-500 break-words">
                  {assessmentDetails?.vqa === null
                    ? assessmentDetails?.provider?.provider_name
                    : assessmentDetails?.vqa?.provider?.provider_name}
                </p>
                <p className="font-semibold text-md">
                  {assessmentDetails?.vqa_request_sponsors
                    ?.filter((sponsor) => sponsor?.is_primary_sponsor === true)
                    ?.map((item) => item?.sponsor_name)}{" "}
                </p>
                <p className="text-sm text-gray-500 ">
                  Request Date:{" "}
                  <span className="font-semibold">
                    {assessmentDetails?.vqa === null
                      ? dateFormat(assessmentDetails?.vqa_request_created_on)
                      : dateFormat(assessmentDetails?.vqa?.vqa_created_on)}
                  </span>
                </p>
              </span>
            </div>
            <div>
              {assessmentDetails?.vqa?.vqa_status === "Canceled Assessment" && (
                <CustomButton
                  text={assessmentDetails?.vqa?.vqa_status || "-"}
                  width="w-[160px]"
                  height="h-10"
                  weight="font-semibold"
                  size="text-md"
                  color="bg-red-400"
                  textColor="text-white"
                  borderRadius="rounded-md"
                  border="none"
                  disabled={true}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="mb-28 flex flex-col custom-breakpoint-8:flex-row gap-6 custom-breakpoint-8:gap-7 custom-breakpoint-7:gap-7 custom-breakpoint-9:gap-8 items-stretch">
        {isLoading ? (
          <div className="w-full custom-breakpoint-8:flex-1 custom-breakpoint-8:min-w-0 pb-6 custom-breakpoint-8:pb-0 custom-breakpoint-8:pr-5 custom-breakpoint-7:pr-6 custom-breakpoint-9:pr-8 border-b custom-breakpoint-8:border-b-0 custom-breakpoint-8:border-r border-gray-300 px-1 sm:px-3">
            <AdminDetailsLoader />
          </div>
        ) : (
          <div className="w-full custom-breakpoint-8:flex-1 custom-breakpoint-8:min-w-0 pb-6 custom-breakpoint-8:pb-0 custom-breakpoint-8:pr-5 custom-breakpoint-7:pr-6 custom-breakpoint-9:pr-8 border-b custom-breakpoint-8:border-b-0 custom-breakpoint-8:border-r border-gray-300 px-1 sm:px-3">
            <div className=" border-b border-gray-300">
              <h1 className="text-sm font-medium text-gray-900">
                Assessment Details
              </h1>
            </div>
            <div className="my-5">
              <StatusProgressBar
                status={
                  assessmentDetails?.vqa !== null
                    ? assessmentDetails?.vqa?.vqa_status
                    : assessmentDetails?.vqa_request_status
                }
              />
            </div>
            <div className="divide-y divide-gray-300">
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Assessment Type
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.vqa_type !== null
                    ? assessmentDetails?.vqa_type
                        ?.slice(0, 1)[0]
                        ?.toUpperCase() + assessmentDetails?.vqa_type?.slice(1)
                    : "-"}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Services in Scope
                </h2>
                <span className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {assessmentDetails?.capabilities?.length > 0 && (
                      <Capabilities
                        services={assessmentDetails?.capabilities}
                        icon={true}
                        showLimited={false}
                      />
                    )}
                    {assessmentDetails?.additional_scope?.length > 0 && (
                      <>
                        <div className="h-5 w-px bg-gray-300 mx-1" />
                        {assessmentDetails.additional_scope.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300"
                          >
                            {tag}
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                </span>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">Location</h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <MapPinIcon className="w-4 h-4 fill-indigo-600 text-indigo-600" />
                  </span>
                  {(assessmentDetails?.vqa_location?.is_secondary_location_required
                      ? assessmentDetails?.vqa_location?.secondary_location_address
                      : null)
                    || getVQALocationFromProvider(assessmentDetails?.provider, { short: false, abbvState: true })
                    || assessmentDetails?.other_address
                    || ""}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">Auditor</h2>
                <p
                  // onClick={() => setDrawerOpen(true)}
                  className="mt-1 text-sm/6 text-indigo-500 sm:col-span-2 sm:mt-0"
                >
                  {assessmentDetails?.vqa?.auditor?.vqa_auditor_name?.length > 1
                    ? assessmentDetails?.vqa?.auditor?.vqa_auditor_name
                    : "-"}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Vendor Contact
                </h2>
                <p className="mt-1 text-sm/6 text-indigo-500 sm:col-span-2 sm:mt-0">
                  <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                    <span className="mr-2">
                      <Icon
                        icon="fa-solid:address-book"
                        className="w-4 h-4 text-black-600"
                      />
                    </span>
                    {assessmentDetails?.vqa === null
                      ? assessmentDetails?.provider?.provider_contact_name
                      : assessmentDetails?.vqa?.provider
                          ?.provider_contact_name}{" "}
                    | {assessmentDetails?.provider?.provider_contact_email}
                  </p>
                </p>
              </div>
              <div
                className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 "
                style={{
                  display: assessmentDetails?.vqa_request_sponsors
                    ? ""
                    : "none",
                }}
              >
                <h2 className="text-sm font-medium text-gray-900">
                  Sponsor Contact
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 items-center">
                  {assessmentDetails?.vqa_request_sponsors?.map(
                    (item, index) => (
                      <span key={index} className="flex items-center space-x-2">
                        <Icon
                          icon="fa-solid:address-book"
                          className="w-4 h-4 text-black-600"
                        />
                        <div>
                          {item?.sponsor_contact_name} | {item?.sponsor_contact_email}
                        </div>
                      </span>
                    )
                  )}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Start Date
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                  </span>
                  {assessmentDetails?.vqa === null
                    ? "-"
                    : assessmentDetails?.vqa?.assessment_start_date
                    ? dateFormat(assessmentDetails?.vqa?.assessment_start_date)
                    : "-"}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Requested Draft Report Date
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                  </span>
                  {assessmentDetails?.desired_initial_draft_date === null
                    ? "-"
                    : dateFormat(assessmentDetails?.desired_initial_draft_date)}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Agreed Draft Date
                </h2>
                <p className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                  <span className="mr-2">
                    <CalendarDaysIcon className="w-4 h-4 text-indigo-600" />
                  </span>
                  {assessmentDetails?.vqa === null
                    ? "-"
                    : assessmentDetails?.vqa?.agreed_initial_draft_date
                    ? dateFormat(
                        assessmentDetails?.vqa?.agreed_initial_draft_date
                      )
                    : "-"}
                </p>
              </div>

              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Additional Info
                </h2>
                <p className="text-sm/6 text-gray-700">
                  {assessmentDetails?.additional_details}
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <ReportAndActivitySkeletonLoader />
        ) : (
          <div className="w-full custom-breakpoint-8:w-full custom-breakpoint-8:max-w-sm custom-breakpoint-7:max-w-md custom-breakpoint-9:max-w-lg custom-breakpoint-8:flex-shrink-0">
            {
              <ReportContainer
                documents={filteredReports}
                status={assessmentDetails?.vqa?.vqa_status}
                vendorName={assessmentDetails?.provider?.provider_name}
              />
            }

            <div className="mt-10 custom-breakpoint-8:mt-6">
              <h2 className="text-sm font-semibold text-gray-900">
                Assessment Activity
              </h2>
              <ul className="mt-6 space-y-6 h-80 overflow-y-auto px-2">
                {assessmentDetails?.vqa === null && (
                  <ul className="relative flex flex-col gap-y-2 mt-1">
                    {activityBeforeAccept?.items
                      .sort(
                        (a, b) =>
                          new Date(b?.activity_done_on) -
                          new Date(a?.activity_done_on)
                      )
                      .map((item, index, array) => {
                        return (
                          <li key={index} className="relative flex gap-x-4">
                            {/* Line connecting items */}
                            {index !== array.length - 1 && (
                              <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                                <div className="w-px bg-gray-200"></div>
                              </div>
                            )}

                            {/* Circle or Profile Picture */}
                            <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                              {item?.activity_comments ? (
                                <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-300">
                                  <span className="text-xs font-medium text-gray-700">
                                    {item?.activity_done_by
                                      ?.charAt(0)
                                      .toUpperCase() || "A"}
                                  </span>
                                </div>
                              ) : (
                                <CheckCircleIcon className="size-6 text-green-600" />
                              )}
                            </div>

                            {/* Activity Description and Comment */}
                            <div className="flex-1">
                              <p className="py-0.5 text-xs text-gray-500">
                                <span className="font-medium text-gray-900">
                                  {!item?.activity_comments &&
                                    item?.activity_done_by}
                                </span>{" "}
                                {item?.activity_description}
                              </p>

                              {item?.activity_comments && (
                                <div className="mt-2 rounded-md border border-gray-300 p-3 text-sm text-gray-800 shadow-sm w-[110%]">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-900 text-xs">
                                      {item?.activity_done_by}{" "}
                                      <span className="py-0.5 text-xs leading-5 text-gray-500 font-normal">
                                        commented
                                      </span>
                                    </span>
                                    <time
                                      dateTime={item?.activity_done_on}
                                      className="flex-none py-0.5 text-xs text-gray-500"
                                    >
                                      {calculateDaysAgo(item?.activity_done_on)}
                                    </time>
                                  </div>
                                  <div className="text-gray-500 text-sm mt-2">
                                    {item?.activity_comments}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Activity Time */}
                            {!item?.activity_comments && (
                              <time
                                dateTime={item?.activity_done_on}
                                className="flex-none py-0.5 text-xs text-gray-500"
                              >
                                {calculateDaysAgo(item?.activity_done_on)}
                              </time>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                )}
                {assessmentDetails?.vqa === null ? (
                  <li className="relative flex gap-x-4 mt-1">
                    {/* Line connecting items */}
                    <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                      <div className="w-px bg-gray-200"></div>
                    </div>

                    {/* Circle Indicator */}
                    <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                      <div className="absolute right-0 bottom-0 text-xs text-green-500">
                        <CheckCircleIcon className="size-6 absolute right-0 bottom-0 text-green-600" />
                      </div>
                    </div>

                    <div className="flex-auto flex">
                      <p className="py-0.5 text-xs text-gray-500 ">
                        <span className="font-medium text-gray-900">
                          {
                            assessmentDetails?.vqa_request_sponsors[0]
                              ?.sponsor_name
                          }
                        </span>
                        <span> Requested New Assessment</span>
                      </p>

                      <time className="flex-none py-0.5 text-xs text-gray-500 ">
                        {assessmentDetails?.activity_done_on
                          ? dateFormat(assessmentDetails?.activity_done_on)
                          : "-"}
                      </time>
                    </div>
                  </li>
                ) : activity?.items ? (
                  activity.items
                    .sort(
                      (a, b) =>
                        new Date(b?.activity_done_on) -
                        new Date(a?.activity_done_on)
                    )
                    .map((item, index, array) => {
                      // Get the latest activity (most recent date)
                      const isLatest =
                        item?.activity_done_on ===
                        array?.reduce((latest, current) =>
                          new Date(current?.activity_done_on) >=
                          new Date(latest?.activity_done_on)
                            ? current
                            : latest
                        )?.activity_done_on;

                      return (
                        <li key={index} className="relative flex gap-x-4">
                          {/* Line connecting items */}
                          <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                            <div className="w-px bg-gray-200"></div>
                          </div>

                          {/* Circle or Profile Picture */}
                          <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                            {item?.activity_comments && !isLatest ? (
                              <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-300">
                                <span className="text-xs font-medium text-gray-700">
                                  {item?.activity_done_by
                                    ?.charAt(0)
                                    ?.toUpperCase() || "A"}
                                </span>
                              </div>
                            ) : (
                              <div className="absolute right-0 bottom-0 text-xs text-green-500">
                                <CheckCircleIcon className="size-6 absolute right-0 bottom-0 text-green-600" />
                              </div>
                            )}
                          </div>

                          {/* Activity Description and Comment */}
                          <div className="flex-[1_1_0%]">
                            <p className="py-0.5 text-xs text-gray-500">
                              <span className="font-medium text-gray-900">
                                {!item?.activity_comments &&
                                  item?.activity_done_by}
                              </span>{" "}
                              {item?.activity_description}
                            </p>
                            {item?.activity_comments && (
                              <div className="mt-2 rounded-md border border-gray-300 p-3 text-sm text-gray-800 shadow-sm w-[110%]">
                                <div className="flex justify-between">
                                  <span className="font-medium text-gray-900 text-xs">
                                    {item?.activity_done_by}{" "}
                                    <span className="py-0.5 text-xs leading-5 text-gray-500 font-normal">
                                      commented
                                    </span>
                                  </span>
                                  <time
                                    dateTime={item?.activity_done_on}
                                    className="flex-none py-0.5 text-xs text-gray-500"
                                  >
                                    {calculateDaysAgo(item?.activity_done_on)}
                                  </time>
                                </div>
                                <div className="text-gray-500 text-sm mt-2">
                                  {item?.activity_comments}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Activity Time */}
                          <time
                            dateTime={item?.activity_done_on}
                            className="flex-none py-0.5 text-xs text-gray-500"
                          >
                            {dateFormat(item?.activity_done_on)}
                          </time>
                        </li>
                      );
                    })
                ) : null}
              </ul>
              <div className="border border-gray-300 rounded-md mt-10 p-4">
                <div className="flex flex-col gap-4">
                  <input
                    placeholder="Add your comment..."
                    name="comment"
                    id="comment"
                    className="border-hidden w-full focus:outline-none focus:border-transparent focus:ring-0 rounded-md text-sm"
                    onChange={(e) => handleChange(e)}
                    value={comment}
                  />

                  <div className="self-end">
                    <CustomButton
                      color="bg-white"
                      size="text-sm" // Adjusted size to better match button
                      weight="font-medium"
                      height="h-9"
                      width="w-24"
                      textColor="text-black"
                      text="Comment"
                      border="border border-gray-300"
                      onClick={addComment}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ProfileDrawer open={drawerOpen} setOpen={setDrawerOpen} />
    </div>
  );
};
